import { useMemo, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetPackagesQuery } from 'client/modules/trial-package/services/hooks/useGetPackagesQuery';
import { UserRoutes } from 'client/modules/users/RouteHelpers';
import useHasEditAccess from 'client/modules/users/hooks/useHasEditAccess';
import UpdateFeatureUsage from 'client/modules/users/components/UpdateFeatureUsage';
import UTCTooltip from 'client/utils/dates';
import { useGetTeamMembersQuery } from '../services/hooks/useGetTeamMembersQuery';
import ActionsDropDown from '../components/ActionsDropDown';
import { FEATURE_IDS } from '../constants/features';

export interface TeamMemberData {
  userId: number;
  name: string;
  email: string;
  package: string;
  startDate: string;
  endDate: string;
  downloadLimit: number;
  downloadsUsed: number;
  admin: boolean;
  owner: boolean;
  expired: boolean;
  tsJoined?: string;
}

export interface FormerTeamMemberData {
  userId: number;
  name: string;
  email: string;
  package: string;
  startDate: string;
  endDate: string;
}

const compareStrings = (a?: string, b?: string) =>
  (a ?? '').localeCompare(b ?? '');

const compareDates = (a?: string, b?: string) =>
  moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf();

const leaveOnlyMatchedUsers = (filter: string) => (teamMember) =>
  filter === '' ||
  teamMember.firstName.toLowerCase().indexOf(filter.toLowerCase()) >= 0 ||
  teamMember.lastName.toLowerCase().indexOf(filter.toLowerCase()) >= 0 ||
  teamMember.email.toLowerCase().indexOf(filter.toLowerCase()) >= 0 ||
  String(teamMember.idUser).indexOf(filter) >= 0;

export const useTeamMembers = (idTeam: number) => {
  const {
    data: teamMembers,
    isLoading: isLoadingTeamMembers,
    refetch: refetchGetTeamMembers,
  } = useGetTeamMembersQuery({
    enabled: Boolean(idTeam),
    params: { idTeam, getOldTeamMembers: true },
  });
  const { data: packagesData } = useGetPackagesQuery({
    enabled: true,
    params: {
      include_deleted: true,
    },
  });

  const hasEditAccess = useHasEditAccess();

  const packageFilters = useMemo(() => {
    const names = new Set<string>();

    teamMembers?.currentTeamMembers.forEach((member) => {
      const packageName = packagesData?.packages.find(
        (packageInfo) => packageInfo.id_package === member.idPackage
      )?.name;

      if (packageName) {
        names.add(packageName);
      }
    });

    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ text: name, value: name }));
  }, [teamMembers, packagesData]);

  const columns: ColumnsType<TeamMemberData> = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      sorter: (a, b) => a.userId - b.userId,
      render: (userId) => (
        <Link className="underline" to={`${UserRoutes.PageIndex}/${userId}`}>
          {userId}
        </Link>
      ),
    },
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => compareStrings(a.name, b.name),
      render: (text, { email, admin, owner }) => (
        <div className="flex justify-between">
          <div>
            <strong>{text}</strong>
            <div>{email}</div>
          </div>
          <div>
            {owner && <Tag color="#2db7f5">Owner</Tag>}
            {admin && <Tag color="#87d068">Admin</Tag>}
          </div>
        </div>
      ),
    },
    {
      title: 'Package',
      dataIndex: 'package',
      key: 'package',
      sorter: (a, b) => compareStrings(a.package, b.package),
      filters: packageFilters,
      onFilter: (value, record) => record.package === value,
      render: (text, { expired }) => ({
        props: {
          style: {
            fontWeight: expired ? 'bold' : 'normal',
          },
        },
        children: text,
      }),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: (a, b) => compareDates(a.endDate, b.endDate),
    },
    {
      title: 'Joined Team',
      dataIndex: 'tsJoined',
      key: 'tsJoined',
      render: (tsJoined) => <UTCTooltip date={tsJoined} />,
      sorter: (a, b) => compareDates(a.tsJoined, b.tsJoined),
    },
    {
      title: 'Downloads',
      dataIndex: 'downloadLimit',
      key: 'downloadLimit',
      sorter: (a, b) => a.downloadLimit - b.downloadLimit,
      render: (downloadLimit, { downloadsUsed, userId }) => (
        <div>
          <Tooltip title="Used/Limit">{`${downloadsUsed}/${downloadLimit}`}</Tooltip>
          <UpdateFeatureUsage
            idFeature={FEATURE_IDS.downloads}
            userId={userId}
            featureName="Downloads"
            usage={downloadsUsed}
            userLimit={downloadLimit}
            teamId={idTeam}
            refetchFeatureUsage={refetchGetTeamMembers}
          />
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      align: 'center',
      render: (text, record) => {
        return (
          <ActionsDropDown
            userId={record.userId}
            isAdmin={record.admin}
            refetchTeamMembers={refetchGetTeamMembers}
            email={record.email}
            teamId={idTeam}
            disabled={!hasEditAccess}
          />
        );
      },
    },
  ];

  const formerColumns: ColumnsType<FormerTeamMemberData> = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      sorter: (a, b) => a.userId - b.userId,
      render: (userId) => (
        <Link className="underline" to={`${UserRoutes.PageIndex}/${userId}`}>
          {userId}
        </Link>
      ),
    },
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => compareStrings(a.name, b.name),
      render: (text, { email }) => (
        <>
          <strong>{text}</strong>
          <div>{email}</div>
        </>
      ),
    },
    {
      title: 'Package',
      dataIndex: 'package',
      key: 'package',
      sorter: (a, b) => compareStrings(a.package, b.package),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: (a, b) => compareDates(a.startDate, b.startDate),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: (a, b) => compareDates(a.endDate, b.endDate),
    },
  ];

  const [teamMemberFilter, setTeamMemberFilter] = useState('');

  const data: TeamMemberData[] = useMemo(() => {
    if (!teamMembers) {
      return [];
    }

    return teamMembers.currentTeamMembers
      .filter(leaveOnlyMatchedUsers(teamMemberFilter))
      .map((member) => {
        const packageName = packagesData?.packages.find(
          (packageInfo) => packageInfo.id_package === member.idPackage
        )?.name;

        return {
          userId: member.idUser,
          email: member.email,
          endDate: moment(member.packageEnd).format('MM-DD-YYYY'),
          startDate: moment(member.packageStart).format('MM-DD-YYYY'),
          name: `${member.firstName} ${member.lastName}`,
          package: packageName,
          downloadLimit: member.downloads.quota,
          downloadsUsed: member.downloads.usage,
          tsJoined: member.tsJoined,
          admin: member.adminPriv,
          owner: member.isOwner,
          expired: packageName === 'Expired Package',
        };
      });
  }, [teamMembers, packagesData, teamMemberFilter]);

  const formerData: FormerTeamMemberData[] = useMemo(() => {
    if (!teamMembers) {
      return [];
    }

    return teamMembers.oldTeamMembers.map((member) => {
      const packageName = packagesData?.packages.find(
        (packageInfo) => packageInfo.id_package === member.idPackage
      )?.name;

      return {
        userId: member.idUser,
        email: member.email,
        endDate: moment(member.packageEnd).format('MM-DD-YYYY'),
        startDate: moment(member.packageStart).format('MM-DD-YYYY'),
        name: `${member.firstName} ${member.lastName}`,
        package: packageName,
      };
    });
  }, [teamMembers, packagesData, teamMemberFilter]);

  return {
    data,
    formerData,
    formerColumns,
    columns,
    refetchGetTeamMembers,
    setTeamMemberFilter,
    isLoadingTeamMembers,
  };
};
